import { forwardRef } from "react";

/**
 * ProphecyShareCard — hidden 1080×1350 (4:5) social card.
 *
 * IMPORTANT
 *  - Rendered off-screen (`position: fixed; left: -99999px`) so it doesn't
 *    affect the visible layout, but stays in the DOM so html-to-image can
 *    capture it.
 *  - Styles are almost fully inlined here (rather than in the stylesheet)
 *    so the html-to-image SVG foreignObject clone renders reliably. Some
 *    modern CSS features (`color-mix`, `isolation`, custom filters …)
 *    render inconsistently through foreignObject, so we keep things
 *    intentionally basic: solid rgba(), simple gradients, plain drop-shadows.
 *  - Frontend-only, no external requests. Uses local WebP assets already
 *    served by the site.
 */

function toRGBA(hex, alpha = 1) {
  if (!hex || typeof hex !== "string") return `rgba(220,230,250,${alpha})`;
  const h = hex.replace("#", "");
  const bigint = parseInt(
    h.length === 3
      ? h.split("").map((c) => c + c).join("")
      : h,
    16,
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

export const ProphecyShareCard = forwardRef(function ProphecyShareCard(
  { family, prophecy },
  ref,
) {
  const accent = family?.accent || "#d9e0ee";
  // Center visual on the generated card is the family's NFT portrait
  // (from the Collection carousel), circular-masked and placed inside
  // the existing family-colored halo. Falls back to the family symbol
  // if no NFT image is mapped for the entry.
  const portraitSrc = family?.nftImage || family?.image;
  // Family label uses natural sentence-style casing ("The Helix have chosen you")
  // so it reads as a secondary caption under the prominent prophecy quote.
  const familyLabel = family?.name
    ? `${family.name} have chosen you`
    : "";

  const rootStyle = {
    // Kept in the viewport at (0,0) but fully hidden and non-interactive.
    // html-to-image needs a real box in the coordinate space to measure &
    // clone reliably — pushing the element far off-screen (`left: -99999px`)
    // makes the SVG foreignObject render as an empty rect.
    position: "fixed",
    top: 0,
    left: 0,
    width: "1080px",
    height: "1350px",
    opacity: 0,
    pointerEvents: "none",
    zIndex: -1,
    padding: "90px 90px 70px",
    boxSizing: "border-box",
    color: "#ffffff",
    fontFamily: '"Michroma", "Inter", sans-serif',
    background: `radial-gradient(1000px 700px at 50% 22%, ${toRGBA(accent, 0.12)}, transparent 65%), radial-gradient(700px 500px at 50% 78%, rgba(120,140,190,0.06), transparent 70%), linear-gradient(180deg, #05070d 0%, #0a0c14 50%, #05070d 100%)`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    border: `1px solid ${toRGBA(accent, 0.22)}`,
  };

  const cornerBase = {
    position: "absolute",
    width: "46px",
    height: "46px",
    borderColor: "rgba(230,235,245,0.55)",
    borderStyle: "solid",
    borderWidth: 0,
  };

  return (
    <div
      ref={ref}
      data-testid="prophecy-share-card"
      aria-hidden="true"
      style={rootStyle}
    >
      {/* Corner brackets */}
      <span style={{ ...cornerBase, top: 40, left: 40, borderTopWidth: 1, borderLeftWidth: 1 }} />
      <span style={{ ...cornerBase, top: 40, right: 40, borderTopWidth: 1, borderRightWidth: 1 }} />
      <span style={{ ...cornerBase, bottom: 40, left: 40, borderBottomWidth: 1, borderLeftWidth: 1 }} />
      <span style={{ ...cornerBase, bottom: 40, right: 40, borderBottomWidth: 1, borderRightWidth: 1 }} />

      {/* Ambient orbital rings (concrete rgba, no color-mix) */}
      <span
        style={{
          position: "absolute", left: "50%", top: "50%",
          width: 900, height: 900,
          border: "1px solid rgba(220,230,250,0.06)",
          borderRadius: 999,
          transform: "translate(-50%,-50%)",
        }}
      />
      <span
        style={{
          position: "absolute", left: "50%", top: "50%",
          width: 1220, height: 1220,
          border: "1px solid rgba(220,230,250,0.05)",
          borderRadius: 999,
          transform: "translate(-50%,-50%)",
        }}
      />

      {/* Top brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 10 }}>
        <img
          src="/img/prophets_logo_mark.webp"
          alt=""
          style={{ width: 44, height: 44, objectFit: "contain", filter: "drop-shadow(0 0 10px rgba(200,215,245,0.28))" }}
        />
        <span
          style={{
            fontFamily: '"Michroma", "Inter", sans-serif',
            fontSize: 22,
            letterSpacing: "0.42em",
            color: "rgba(255,255,255,0.9)",
          }}
        >
          PROPHETS
        </span>
      </div>

      {/* Family NFT portrait — circular-masked and centered inside the
          family-colored halo. Fills the halo edge-to-edge so it reads as
          an "outer colored aura + inner circular NFT portrait". */}
      <div
        style={{
          position: "relative",
          marginTop: 110,
          width: 460, height: 460,
          borderRadius: 999,
          background: "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.05), rgba(0,0,0,0.6) 65%)",
          border: `1px solid ${toRGBA(accent, 0.22)}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* halo */}
        <span
          style={{
            position: "absolute",
            top: -22, left: -22, right: -22, bottom: -22,
            borderRadius: 999,
            border: `1px solid ${toRGBA(accent, 0.30)}`,
            boxShadow: `0 0 60px ${toRGBA(accent, 0.18)}`,
          }}
        />
        {portraitSrc ? (
          <img
            src={portraitSrc}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 30%",
              borderRadius: 999,
              boxShadow: `0 0 40px ${toRGBA(accent, 0.28)}, inset 0 0 30px rgba(0,0,0,0.55)`,
            }}
          />
        ) : null}
      </div>

      {/* Body — prophecy quote is the primary focal text. The family line
          used to live under it as a caption; it's now moved to the footer
          to avoid duplication and give the quote room to breathe. */}
      <div style={{ marginTop: 96, width: "100%", padding: "0 20px" }}>
        <span
          style={{
            display: "block",
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: 18,
            letterSpacing: "0.42em",
            color: "rgba(255,255,255,0.55)",
          }}
        >
          YOUR PROPHECY
        </span>
        <h2
          style={{
            margin: "26px auto 0",
            maxWidth: 860,
            fontSize: 46,
            letterSpacing: "0.06em",
            lineHeight: 1.22,
            color: "#ffffff",
            fontFamily: '"Michroma", "Inter", sans-serif',
            textTransform: "uppercase",
            textShadow: `0 0 30px ${toRGBA(accent, 0.28)}`,
          }}
        >
          {prophecy ? `\u201C${prophecy}\u201D` : ""}
        </h2>
      </div>

      {/* Footer — replaced the static "10 FAMILIES · COUNTLESS FUTURES"
          tagline with the dynamic family line so the card closes on the
          selected family. */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 22,
        }}
      >
        <span style={{ display: "inline-block", width: 40, height: 1, background: "rgba(220,230,250,0.35)" }} />
        <span
          style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: 22,
            letterSpacing: "0.18em",
            color: "rgba(230,236,248,0.85)",
          }}
        >
          {familyLabel}
        </span>
        <span style={{ display: "inline-block", width: 40, height: 1, background: "rgba(220,230,250,0.35)" }} />
      </div>
    </div>
  );
});
